import React, { useEffect, useState } from 'react';

import {
  extractRequestSpecs,
  formatAspectRatio,
  formatDuration,
  formatFileType,
  MediaSpec,
} from '@/lib/mediaSpecs';

interface Measured {
  width?: number;
  height?: number;
  duration?: number;
}

/**
 * Results are cached per URL so a remount (StrictMode's double-invoke, a hot
 * reload, a return visit) reuses a completed measurement instead of probing
 * the file again.
 */
const measuredCache = new Map<string, Measured>();

/**
 * Reads the real dimensions (and, for clips, the duration) off the delivered
 * file. The stored request JSON is too inconsistent across providers to rely
 * on — see the note in `@/lib/mediaSpecs`. The probe is cheap: an <img> hits
 * the browser cache for the file the page is already showing, and a detached
 * <video preload="metadata"> only fetches the container header.
 */
const useMeasuredMedia = (url?: string, isVideo = false): Measured => {
  const [measured, setMeasured] = useState<Measured>({});

  useEffect(() => {
    if (!url) return;

    const cached = measuredCache.get(url);
    if (cached) {
      setMeasured(cached);
      return;
    }

    let cancelled = false;
    const commit = (next: Measured) => {
      measuredCache.set(url, next);
      if (!cancelled) setMeasured(next);
    };

    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      const onLoaded = () =>
        commit({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        });
      video.addEventListener('loadedmetadata', onLoaded);
      video.src = url;
      return () => {
        cancelled = true;
        video.removeEventListener('loadedmetadata', onLoaded);
      };
    }

    const image = new window.Image();
    const onLoad = () =>
      commit({ width: image.naturalWidth, height: image.naturalHeight });
    image.addEventListener('load', onLoad);
    image.src = url;
    return () => {
      cancelled = true;
      image.removeEventListener('load', onLoad);
    };
  }, [url, isVideo]);

  return measured;
};

/**
 * The spec list on the public image/video detail pages — resolution, aspect
 * ratio, duration, file type and whatever else the row can tell us. Renders
 * nothing until at least one spec is known, so a row with no usable metadata
 * doesn't leave an empty heading behind.
 */
const MediaSpecs = ({
  url,
  isVideo = false,
  request,
  usedCredits,
}: {
  url?: string;
  isVideo?: boolean;
  request?: unknown;
  usedCredits?: number | null;
}) => {
  const { width, height, duration } = useMeasuredMedia(url, isVideo);

  const specs: MediaSpec[] = [];

  if (width && height) {
    specs.push({ label: 'Resolution', value: `${width} × ${height}` });
    const ratio = formatAspectRatio(width, height);
    if (ratio) specs.push({ label: 'Aspect ratio', value: ratio });
  }

  const durationLabel = formatDuration(duration);
  if (durationLabel) specs.push({ label: 'Duration', value: durationLabel });

  specs.push(...extractRequestSpecs(request, isVideo));

  const fileType = formatFileType(url);
  if (fileType) specs.push({ label: 'File', value: fileType });

  if (usedCredits) {
    specs.push({
      label: 'Credits',
      value: `${usedCredits} ${usedCredits === 1 ? 'credit' : 'credits'}`,
    });
  }

  if (specs.length === 0) return null;

  return (
    <div className='mt-6'>
      <h2 className='text-sm font-semibold text-gray-800 dark:text-slate-300'>
        Details
      </h2>
      <dl className='mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3'>
        {specs.map((spec) => (
          <div key={spec.label}>
            <dt className='text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500'>
              {spec.label}
            </dt>
            <dd className='mt-0.5 text-sm font-medium text-gray-800 dark:text-slate-200'>
              {spec.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default MediaSpecs;
